import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import {
  DeviceInfoPayload,
  GeoLocationPayload,
} from './types/contracts.type';

async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Fallback (e.g. very old browsers): Web Crypto missing → poor-quality FNV-1a hash padded to 64 chars.
  // This is not cryptographically meaningful, but the backend only stores it for forensic continuity, not auth.
  // Uses the BigInt() constructor instead of literal syntax to remain compatible with pre-ES2020 targets.
  const FNV_OFFSET = BigInt('0xcbf29ce484222325');
  const FNV_PRIME = BigInt('0x100000001b3');
  const MASK64 = BigInt('0xffffffffffffffff');
  let h = FNV_OFFSET;
  for (let i = 0; i < input.length; i++) {
    h ^= BigInt(input.charCodeAt(i));
    h = (h * FNV_PRIME) & MASK64;
  }
  return h.toString(16).padStart(64, '0').slice(0, 64);
}

export interface SignatureContext {
  deviceInfo: DeviceInfoPayload;
  deviceFingerprint: string;
  geoLocation: GeoLocationPayload | null;
}

export async function collectSignatureContext(): Promise<SignatureContext> {
  const info = await Device.getInfo();
  const screen = {
    w: window.screen.width,
    h: window.screen.height,
    dpr: window.devicePixelRatio || 1,
  };
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userAgent = navigator.userAgent;

  const deviceInfo: DeviceInfoPayload = {
    platform: info.platform,
    model: info.model,
    osVersion: info.osVersion,
    screen,
    timezone,
    userAgent,
  };

  let geoLocation: GeoLocationPayload | null = null;
  try {
    const pos = await Promise.race([
      Geolocation.getCurrentPosition({ timeout: 5000 }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('geolocation timeout')), 5500),
      ),
    ]);
    geoLocation = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    };
  } catch {
    // user denied / timed out / unavailable — proceed with null
  }

  const deviceFingerprint = await sha256Hex(
    JSON.stringify({
      platform: deviceInfo.platform,
      model: deviceInfo.model,
      osVersion: deviceInfo.osVersion,
      screen: deviceInfo.screen,
      timezone: deviceInfo.timezone,
      userAgent: deviceInfo.userAgent,
    }),
  );

  return { deviceInfo, deviceFingerprint, geoLocation };
}
